import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { OnboardingData } from "../../../hooks/useOnboardingState";
import { getCsrf } from "../../../api";
import { authApi } from "../../../services/auth";
import GoogleButton from "../../../components/GoogleButton";
// Static import — dynamic import() chunks never load in the Capacitor
// WKWebView (see src/lib/nativePush.ts). Guarded by isNative, which is
// iOS-only — @capacitor-community/apple-sign-in has no Android implementation.
import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import { appleSignInAvailable, isIosNative } from "../../../lib/appleSignIn";
import { useSignupTipsBox } from "../../../hooks/useSignupTipsBox";
import type { SignupNotice, SignupPrecheck } from "../../../types/auth";
import { t } from "../../../i18n";
import { T } from "../../../i18n/T";
import "./Step1Welcome.css";

interface Props {
  data: OnboardingData;
  updateData: (updates: Partial<OnboardingData>) => void;
  onNext: () => void;
}

// Connect Instagram, its own step before the bank (build 54), only when the
// server says so for this sign-up (both Instagram switches on; older servers
// never say it). A new sign-in starts with no Instagram claim.
const instagramStepOf = (pre: SignupPrecheck | undefined): Partial<OnboardingData> => ({
  igStep: pre?.instagramStep === true,
  igClaim: "",
  igUsername: "",
});

export default function Step1Welcome({ data, updateData, onNext }: Props) {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // App-tips emails: "Don't email me app tips", "Email me app tips" where the
  // law wants an explicit yes, or no box where it needs no choice (by the
  // phone's timezone; no box until the server answers).
  const tipsBox = useSignupTipsBox();
  const tipsOptIn = tipsBox.kind === "opt_in";
  // The box on screen when they tapped Continue with Google. Google's web
  // button answers seconds later, through the newest render, so a tap before
  // the box came would otherwise stamp a box they never saw.
  const boxAtGoogleTap = useRef<SignupNotice | null>(null);

  const handleGoogle = async (credential: string) => {
    setError("");
    setLoading(true);
    try {
      await getCsrf();
      const pre = await authApi.googlePrecheck(credential);

      if (pre?.exists) {
        // Already-registered talent re-signing in here (e.g. they tapped the
        // welcome email then re-authed): send them to /login to resume Stripe
        // rather than walking them back through signup into a duplicate-account
        // error. routeAfterLogin picks up needsStripeFinish → /finish-stripe.
        window.location.href = "/login";
        return;
      }

      updateData({
        authMethod: "google",
        googleCredential: credential,
        appleCredential: null,
        name: pre?.name || "",
        email: pre?.email || "",
        // The app-tips box below, the one they saw on the way in (null: none).
        emailNotice: (boxAtGoogleTap.current || tipsBox).notice,
        // Connect Instagram gets its own step (build 54): the server's word for this sign-up.
        ...instagramStepOf(pre),
      });

      onNext();
    } catch (e: any) {
      setError(e?.response?.data?.msg || t("step1Welcome.googleSignInFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleApple = async () => {
    setError("");
    setLoading(true);

    const isNative = isIosNative();

    try {
      if (isNative) {
        const result = await SignInWithApple.authorize({
          clientId: process.env.REACT_APP_APPLE_CLIENT_ID || "com.requesta.signin",
          redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI || "https://www.requesta.app/auth/apple/callback",
          scopes: "email name",
        });

        if (result?.response?.identityToken) {
          // Same existing-account guard as Google (handleGoogle above): if this
          // Apple ID already has an account, resume Stripe via /login instead of
          // re-onboarding into a duplicate-account error. /auth/apple/precheck
          // already returns `exists`. Returning users get no email from Apple, so
          // the server matches on appleId — don't change that.
          await getCsrf();
          const pre = await authApi.applePrecheck(result.response.identityToken);
          if (pre?.exists) {
            window.location.href = "/login";
            return;
          }
          updateData({
            authMethod: "apple",
            appleCredential: {
              idToken: result.response.identityToken,
              user: {
                name: result.response.givenName
                  ? `${result.response.givenName} ${result.response.familyName || ""}`.trim()
                  : undefined,
                email: result.response.email || undefined,
              }
            },
            googleCredential: null,
            name: result.response.givenName
              ? `${result.response.givenName} ${result.response.familyName || ""}`.trim()
              : "",
            email: result.response.email || "",
            emailNotice: tipsBox.notice,
            ...instagramStepOf(pre),
          });
          onNext();
        }
      } else {
        // Web fallback
        if (!window.AppleID) {
          // Load SDK if not loaded
          const script = document.createElement("script");
          script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
          script.async = true;
          document.head.appendChild(script);
          await new Promise(resolve => script.onload = resolve);
        }

        window.AppleID?.auth.init({
          clientId: process.env.REACT_APP_APPLE_CLIENT_ID || "com.requesta.signin",
          scope: "email name",
          redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI || "https://www.requesta.app/auth/apple/callback",
          usePopup: true,
        });

        const res = await window.AppleID?.auth.signIn();
        if (res?.authorization?.id_token) {
          // Existing-account guard — mirrors the native branch above.
          await getCsrf();
          const pre = await authApi.applePrecheck(res.authorization.id_token);
          if (pre?.exists) {
            window.location.href = "/login";
            return;
          }
          updateData({
            authMethod: "apple",
            appleCredential: {
              idToken: res.authorization.id_token,
              user: {
                name: res.user?.name
                  ? `${res.user.name.firstName || ""} ${res.user.name.lastName || ""}`.trim()
                  : undefined,
                email: res.user?.email || undefined,
              }
            },
            googleCredential: null,
            name: res.user?.name
              ? `${res.user.name.firstName || ""} ${res.user.name.lastName || ""}`.trim()
              : "",
            email: res.user?.email || "",
            emailNotice: tipsBox.notice,
            ...instagramStepOf(pre),
          });
          onNext();
        }
      }
    } catch (err: any) {
      if (err?.message !== "The user canceled the sign-in flow." && err?.error !== "popup_closed_by_user") {
        setError(t("step1Welcome.appleSignInFailed"));
        console.error("Apple Sign In error:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="onboarding-step step1-welcome">
      <p className="step1-signup-label">{t("step1Welcome.signUpToday")}</p>
      <h1 className="step1-headline">
        <T k="step1Welcome.turnRequestsToIncome" tags={{ 0: (c) => <em>{c}</em> }} />
      </h1>

      {/* DJ Lucky's demo pass — what they're signing up for. The name is
          generic on purpose: a brand-new signup has no DJ name yet. */}
      <div className="step1-stage" aria-hidden="true">
        {/* Twinkling sparkles around the pass — echoes the star in the
            wordmark (founder-kept from the old welcome screen). */}
        <span className="step1-stage-sparkle step1-stage-sparkle-1">✦</span>
        <span className="step1-stage-sparkle step1-stage-sparkle-2">✦</span>
        <span className="step1-stage-sparkle step1-stage-sparkle-3">✦</span>
        {/* Mini replica of the in-app green wallet pass (walletPassRenderer.ts
            "green" theme): waves + corner-R texture, white QR box, black name.
            Decorative — DJ LUCKY is the demo DJ, not a real account. */}
        <div className="step1-pass">
          <svg className="step1-pass-waves" viewBox="0 0 80 181" fill="none">
            <path d="M-10 30 C 20 45, 20 75, -10 95" stroke="#1DA550" strokeWidth="2" opacity="0.55" />
            <path d="M-10 60 C 26 78, 26 110, -10 130" stroke="#1DA550" strokeWidth="2" opacity="0.4" />
            <path d="M-10 95 C 30 115, 30 150, -10 168" stroke="#1DA550" strokeWidth="2" opacity="0.3" />
          </svg>
          <img src="/r.png" alt="" className="step1-pass-corner-r" />
          <img src="/request-wordmark.png" alt="" className="step1-pass-wordmark" />
          <div className="step1-pass-qrbox">
            <img src="/demo-qr.png" alt="" className="step1-pass-qr" />
            <span className="step1-pass-center">
              <img src="/r.png" alt="" />
            </span>
          </div>
          <div className="step1-pass-name">{t("step1Welcome.djName")}</div>
          <div className="step1-pass-price">{t("step1Welcome.perRequest")}</div>
        </div>
      </div>

      {/* Auth buttons */}
      <div className="step1-auth-buttons">
        <GoogleButton
          onSuccess={handleGoogle}
          onError={() => setError(t("step1Welcome.googleSignInFailed"))}
          onClick={() => {
            boxAtGoogleTap.current = tipsBox;
          }}
          text="continue_with"
        />

        {/* None inside the Android app: it could never finish there (lib/appleSignIn). */}
        {appleSignInAvailable() && (
          <button
            type="button"
            className="step1-apple-btn"
            onClick={handleApple}
            disabled={loading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.51-3.23 0-1.44.64-2.2.45-3.06-.4C3.79 16.17 4.36 9.02 8.82 8.78c1.15.06 1.95.65 2.63.68.99-.2 1.95-.78 3.01-.7 1.28.1 2.24.61 2.87 1.56-2.64 1.57-2.01 5.02.36 5.98-.49 1.3-1.13 2.58-2.64 3.98zM12.03 8.7c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            {t("step1Welcome.continueWithApple")}
          </button>
        )}
      </div>

      {error && <p className="step1-error">{error}</p>}

      {/* Sign-up-wrap consent: adjacent to the auth buttons (the entry gesture).
          The binding copy repeats at the Connect Bank step, where the account is
          actually created. */}
      <p className="onboarding-legal">
        <T k="step1Welcome.byContinuingYouAgree" tags={{ 0: (c) => <Link to="/terms">{c}</Link>, 1: (c) => <Link to="/privacy">{c}</Link> }} />
      </p>

      {/* App-tips emails, asked here before the account exists, only where
          the law needs a choice: an unticked box to refuse (the soft opt-in),
          or to say yes where the law wants an explicit yes. None in the US,
          Canada, Australia and New Zealand, and none until the server has
          answered (no space kept, nothing flashes up and away). Each box keeps
          its own tick; the answer and the box's version ride the register call
          at the bank step (Step6Complete). A box arriving after the first
          paint fades in (keyed row). */}
      {tipsBox.kind !== "none" && (
        <label key={tipsBox.kind} className="step1-tips">
          <input
            type="checkbox"
            className="step1-tips-box"
            checked={tipsOptIn ? !!data.emailTipsOptIn : !!data.emailTipsOptOut}
            onChange={(e) => updateData(tipsOptIn ? { emailTipsOptIn: e.target.checked } : { emailTipsOptOut: e.target.checked })}
          />
          <span className="step1-tips-text">
            {tipsOptIn ? t("step1Welcome.emailMeAppTips") : t("step1Welcome.dontEmailMeAppTips")}
          </span>
        </label>
      )}

      <p className="step1-login-link">
        <T k="step1Welcome.alreadyHaveAnAccount" tags={{ 0: (c) => <Link to="/login">{c}</Link> }} />
      </p>
    </div>
  );
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: any) => void;
        signIn: () => Promise<any>;
      };
    };
  }
}
